import Decimal from 'decimal.js';
import { useEffect, useState } from 'react';

import type {
  OrderDetailsOrder,
  OrderDetailsOrderLine,
} from '@/types/orderDetailsOrder';

import type {
  CheckoutFragment,
  CheckoutLineFragment,
  MoneyFragment,
  OrderFragment,
  OrderLineFragment,
} from '@/graphql/saleor/api';
import {
  excludeOrderType,
  isColoradoBillingTypeForCheckout,
  isColoradoBillingTypeForOrder,
} from '@/lib/order';

type TaxedTotal = {
  currency?: string;
  gross?: MoneyFragment | null;
  net?: MoneyFragment | null;
  tax?: MoneyFragment | null;
};

export const getDisplayedSalesTax = (
  total?: TaxedTotal | null,
  excludedTax?: MoneyFragment | null,
): MoneyFragment | undefined => {
  const reportedTax = total?.tax;
  const gross = total?.gross;
  const net = total?.net;
  let tax = reportedTax;

  if (
    (reportedTax?.amount ?? 0) === 0 &&
    gross?.amount !== undefined &&
    net?.amount !== undefined &&
    gross.amount > net.amount
  ) {
    tax = {
      amount: new Decimal(gross.amount).minus(net.amount).toNumber(),
      currency: reportedTax?.currency ?? gross.currency,
    };
  }

  if (!tax || !excludedTax?.amount) return tax ?? undefined;

  return {
    ...tax,
    amount: Decimal.max(
      new Decimal(tax.amount).minus(excludedTax.amount),
      0,
    ).toNumber(),
  };
};

export const useOrderIsColorado = () => {
  const [coSalesTax, setCoSalesTax] = useState<MoneyFragment>();
  const [salesTax, setSalesTax] = useState<MoneyFragment>();

  const IsTypeCheckout = (order: CheckoutFragment) => {
    const isColoradoBilling = isColoradoBillingTypeForCheckout(order);
    const [orderLines, setOrderLines] = useState<CheckoutLineFragment[]>([]);
    const products = order?.lines?.filter(
      (productVariant: CheckoutLineFragment) =>
        excludeOrderType(
          productVariant?.variant?.product.productType.name as string,
          'buckle',
        ),
    );

    const arr = products?.filter(
      (item: CheckoutLineFragment) =>
        item?.variant?.product.productType.name !== 'Colorado Tax Dummy',
    );

    const coSales = products?.find(
      (item: CheckoutLineFragment) =>
        item?.variant?.product.productType.name === 'Colorado Tax Dummy',
    );

    useEffect(() => {
      if (isColoradoBilling) {
        setOrderLines(arr as CheckoutLineFragment[]);
        setCoSalesTax(coSales?.unitPrice.tax);
        setSalesTax(
          getDisplayedSalesTax(order?.totalPrice, coSales?.unitPrice?.tax),
        );
      } else {
        setOrderLines(arr);
        setSalesTax(getDisplayedSalesTax(order?.totalPrice));
      }
    }, [isColoradoBilling, coSales, order]);

    return { orderLines, coSalesTax, salesTax, isColoradoBilling };
  };

  type ColoradoOrderLinesResult<L> = {
    coSalesTax: MoneyFragment | undefined;
    isColoradoBilling: boolean;
    orderLines: L[];
    salesTax: MoneyFragment | undefined;
  };

  function IsTypeOrder(
    order: OrderDetailsOrder,
  ): ColoradoOrderLinesResult<OrderDetailsOrderLine>;
  function IsTypeOrder(
    order: OrderFragment,
  ): ColoradoOrderLinesResult<OrderLineFragment>;
  function IsTypeOrder(
    order: OrderDetailsOrder | OrderFragment,
  ): ColoradoOrderLinesResult<OrderDetailsOrderLine | OrderLineFragment> {
    const [orderLines, setOrderLines] = useState<
      (OrderDetailsOrderLine | OrderLineFragment)[]
    >([]);
    const isColoradoBilling = isColoradoBillingTypeForOrder(order);
    const orderLineRows = (order.lines ?? []) as ReadonlyArray<
      OrderDetailsOrderLine | OrderLineFragment
    >;
    const products = orderLineRows.filter((productVariant) =>
      excludeOrderType(
        productVariant?.variant?.product.productType.name as string,
        'buckle',
      ),
    );

    const arr = products.filter(
      (item) =>
        item?.variant?.product.productType.name !== 'Colorado Tax Dummy',
    );

    const coSales = products.find(
      (item) =>
        item?.variant?.product.productType.name === 'Colorado Tax Dummy',
    );

    useEffect(() => {
      if (isColoradoBilling) {
        setOrderLines(arr);
        if (coSales?.totalPrice) {
          setCoSalesTax(coSales?.totalPrice.tax);
        } else {
          setCoSalesTax(coSales?.unitPrice.tax);
        }
        setSalesTax(
          getDisplayedSalesTax(order?.total, coSales?.totalPrice?.tax),
        );
      } else {
        setOrderLines(arr);
        setSalesTax(getDisplayedSalesTax(order?.total));
      }
    }, [isColoradoBilling, coSales, order]);

    return {
      orderLines,
      coSalesTax,
      salesTax,
      isColoradoBilling,
    };
  }

  return {
    IsTypeCheckout,
    IsTypeOrder,
  };
};
